/* eslint-disable max-len */
import { useLocationShort } from 'hooks/useLocation';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GLOBAL_CONFIG from '../global-variables';

const useUtils = () => {
  let { btNumber, stNumber } = useParams<{ btNumber: string, stNumber: string }>();
  const { t } = useTranslation();
  const location = useLocationShort(stNumber || btNumber);

  if (GLOBAL_CONFIG.useMdmIds) {
    if (stNumber) stNumber = location.data?.mdm?.site_id || stNumber;
    if (btNumber) btNumber = location.data?.bill_to?.mdm?.site_id || btNumber;
  }

  if (stNumber) btNumber = location.data?.bill_to?.customer_number || btNumber;

  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const urlRegex = /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;

  const convertFromUOM = (value: any, countryCode: string) => {
    const v = (typeof value === 'number' ? value : parseFloat(value));
    const c = ((countryCode || '').toLocaleLowerCase() === 'us' ? 0.000621371 : 0.001);
    return Math.round(v * c).toString();
  };

  const convertToUOM = (value: any, countryCode: string) => {
    const v = (typeof value === 'number' ? value : parseFloat(value));
    const c = (countryCode === 'us' ? 0.000621371 : 0.001);
    return Math.round(v / c).toString();
  };

  const checkLevel = (from: string | undefined, to: string | undefined): boolean => {
    if (!['ST', 'BT', 'HO', 'PC'].includes(from || '')) return false;
    if (!['ST', 'BT', 'HO', 'PC'].includes(to || '')) return false;

    const level = new Map();
    level.set('ST', 0);
    level.set('BT', 1);
    level.set('HO', 2);
    level.set('PC', 3);

    return (level.get(from) >= level.get(to));
  };

  const getValuesByKey = (object: any, keyToFind: string): string[] => {
    const valuesArray: string[] = [];

    Object.entries(object).forEach(([key, value]) => {
      if (key === keyToFind) {
        const message = value as string;
        valuesArray.push(t(message));
        return;
      }
      if (typeof value === 'object' && value !== null) {
        valuesArray.push(...getValuesByKey(value, keyToFind));
      }
    });

    return valuesArray;
  };

  if (location.isLoading) {
    return {
      subTitle: t('loading...'),
      convertFromUOM,
      convertToUOM,
      checkLevel,
      emailRegex,
      urlRegex,
      getValuesByKey,
    };
  }

  const getTitle = () => t('BT: {{btName}} (#{{btNumber}})', {
    btName: location.data?.customer_dba_name || location.data?.customer_name || '',
    btNumber,
  });

  return {
    title: getTitle(),
    subTitle: null,
    convertFromUOM,
    convertToUOM,
    checkLevel,
    emailRegex,
    urlRegex,
    getValuesByKey,
    location,
  };
};

export default useUtils;
